import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Flex, TablesGrid } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { MetricCard } from 'components/ui/MetricCard/MetricCard'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import CalendarInput from 'components/CalendarInput/CalendarInput'
import { AppDispatch, RootState } from 'store'
import {
  finalizeFoodDay,
  finalizeFoodWindow,
  getFoodDashboard,
  getFoodIntentsPreview,
} from 'features/food/foodSlice'
import {
  FoodMealWindowRunStatus,
  FoodPlanGenerationStatus,
  IFoodDietaryTagResponse,
  IFoodWindowSummaryResponse,
  INextDayPreviewRow,
} from 'types/api/foodApi.interface'
import { foodProvisioningModeLabel } from '../foodPlanningData'
import { formatDateForDisplay } from 'utils/helpers'

const today = () => {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

const tomorrow = () => {
  const d = today()
  d.setDate(d.getDate() + 1)
  return d
}

const formatOperationalDay = (d: Date) => {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const windowRunStatusLabel = (status: number | null) => {
  if (status === FoodMealWindowRunStatus.Open) return 'Open'
  if (status === FoodMealWindowRunStatus.Ordered) return 'Ordered'
  if (status === FoodMealWindowRunStatus.Served) return 'Served'
  if (status === FoodMealWindowRunStatus.Finalized) return 'Finalized'
  return 'No run'
}

const windowRunStatusPill = (status: number | null) => {
  if (status === FoodMealWindowRunStatus.Finalized) return 'success'
  if (status === FoodMealWindowRunStatus.Served) return 'info'
  if (status === FoodMealWindowRunStatus.Ordered) return 'accent'
  if (status === FoodMealWindowRunStatus.Open) return 'neutral'
  return 'neutral'
}

const planGenerationLabel = (status: number) => {
  if (status === FoodPlanGenerationStatus.Generated) return 'Generated'
  if (status === FoodPlanGenerationStatus.SkippedNoConfigVersion) return 'Skipped — no config version'
  if (status === FoodPlanGenerationStatus.SkippedNoMealWindows) return 'Skipped — no meal windows'
  if (status === FoodPlanGenerationStatus.Failed) return 'Failed'
  return 'Never ran'
}

const formatDietaryTagSet = (tags: IFoodDietaryTagResponse[]) => {
  if (!tags?.length) return 'Untagged'
  return tags.map((t) => t.code || t.name).filter(Boolean).join(' + ') || 'Untagged'
}

export const DashboardTab = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { dashboard, nextDayPreview, isLoading } = useSelector((store: RootState) => store.food)
  const [day, setDay] = useState<Date>(today())
  const [previewDay, setPreviewDay] = useState<Date>(tomorrow())
  const [finalizing, setFinalizing] = useState(false)

  const refreshDashboard = (d: Date) => {
    dispatch(getFoodDashboard(formatOperationalDay(d)))
  }

  useEffect(() => {
    refreshDashboard(day)
  }, [dispatch, day])

  useEffect(() => {
    dispatch(getFoodIntentsPreview({ day: formatOperationalDay(previewDay) }))
  }, [dispatch, previewDay])

  const handleFinalizeDay = async () => {
    setFinalizing(true)
    const resp = await dispatch(finalizeFoodDay(formatOperationalDay(day)))
    setFinalizing(false)
    if (resp.meta.requestStatus === 'fulfilled') {
      refreshDashboard(day)
    }
  }

  const handleFinalizeWindow = async (runId: number) => {
    const resp = await dispatch(finalizeFoodWindow(runId))
    if (resp.meta.requestStatus === 'fulfilled') {
      refreshDashboard(day)
    }
  }

  const windows = dashboard?.windows || []
  const openExceptions = dashboard?.openExceptions
  const dayClose = dashboard?.dayClose
  const planGeneration = dashboard?.planGeneration

  const totalOpenExceptions = useMemo(
    () =>
      openExceptions
        ? openExceptions.critical + openExceptions.high + openExceptions.medium + openExceptions.low
        : 0,
    [openExceptions],
  )

  const windowColumns: IHonestTableColumn<IFoodWindowSummaryResponse & { id: number }>[] = [
    {
      id: 'window',
      header: 'Window',
      cell: (row) => (
        <Flex column>
          <BodyText weight='600'>{row.windowDef?.name}</BodyText>
          <OtherText grey>
            {`${String(row.windowDef?.serviceStart || '').slice(0, 5)}–${String(row.windowDef?.serviceEnd || '').slice(0, 5)}`}
            {' · cut-off '}
            {String(row.windowDef?.cutoffTime || '').slice(0, 5)}
          </OtherText>
        </Flex>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => (
        <StatusPill status={windowRunStatusPill(row.status)}>
          {windowRunStatusLabel(row.status)}
          {row.mealWindowRun?.lateFire ? ' · late fire' : ''}
        </StatusPill>
      ),
    },
    { id: 'planned', header: 'Planned', cell: (row) => row.planned },
    { id: 'ordered', header: 'Ordered', cell: (row) => row.ordered },
    { id: 'served', header: 'Served', cell: (row) => row.served },
    { id: 'finalized', header: 'Finalized', cell: (row) => row.finalized },
    { id: 'cancelled', header: 'Cancelled', cell: (row) => row.cancelled },
    {
      id: 'expectedWaste',
      header: 'Waste',
      cell: (row) =>
        row.expectedWaste ? (
          <StatusPill status='danger'>{row.expectedWaste}</StatusPill>
        ) : (
          row.expectedWaste
        ),
    },
    {
      id: 'adjustments',
      header: 'Adjustments',
      cell: (row) =>
        row.manualAdjustmentQty
          ? `+${row.manualAdjustmentQty} (${(row.manualAdjustmentReasons || []).join('; ')})`
          : '—',
    },
    {
      id: 'kitchens',
      header: 'Kitchens confirmed',
      cell: (row) => {
        const confirmed = (row.kitchens || []).filter((k) => k.confirmed).length
        return `${confirmed}/${(row.kitchens || []).length}`
      },
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (row) => (
        <Flex gap='0.5rem' justend>
          {row.mealWindowRun && (
            <Button
              size='sm'
              variant='secondary'
              onClick={() => navigate(`/hr/food/window/${row.mealWindowRun.id}`)}
            >
              Open
            </Button>
          )}
          {row.mealWindowRun && row.status !== FoodMealWindowRunStatus.Finalized ? (
            <Button
              size='sm'
              variant='secondary'
              onClick={() => handleFinalizeWindow(row.mealWindowRun.id)}
            >
              Finalize
            </Button>
          ) : null}
        </Flex>
      ),
    },
  ]

  const windowRows = windows.map((w, idx) => ({ ...w, id: w.windowDef?.id ?? idx }))

  const previewColumns: IHonestTableColumn<INextDayPreviewRow & { id: string }>[] = [
    { id: 'kitchen', header: 'Kitchen', cell: (row) => row.kitchen?.name || '—' },
    { id: 'window', header: 'Window', cell: (row) => row.windowDef?.name || '—' },
    { id: 'mode', header: 'Mode', cell: (row) => foodProvisioningModeLabel(row.mode) },
    { id: 'totalCount', header: 'Planned', cell: (row) => row.totalCount },
    {
      id: 'dietaryTagSets',
      header: 'Dietary combinations',
      minWidth: '16rem',
      cell: (row) => (
        <Flex wrap gap='0.25rem'>
          {(row.dietaryTagSets || []).map((set, idx) => (
            <StatusPill key={`${formatDietaryTagSet(set.dietaryTags)}-${idx}`} status='accent'>
              {`${formatDietaryTagSet(set.dietaryTags)} ×${set.count}`}
            </StatusPill>
          ))}
          {!row.dietaryTagSets?.length && <OtherText grey>—</OtherText>}
        </Flex>
      ),
    },
  ]

  const previewRows: (INextDayPreviewRow & { id: string })[] = (nextDayPreview || []).map(
    (row, idx) => ({
      ...row,
      id: `${row.kitchen?.id}-${row.windowDef?.id}-${row.mode}-${idx}`,
    }),
  )

  return (
    <Flex column gap='1rem' width='100%'>
      <Flex between center width='100%' wrap gap='1rem'>
        <CalendarInput
          nomarg
          labelText='Operational day'
          defaultValue={day}
          onChange={(d: Date) => d && setDay(d)}
        />
        <MetricCard
          layout='inline'
          value={totalOpenExceptions}
          label='Open exceptions'
          trailing={
            openExceptions && totalOpenExceptions > 0 ? (
              <Flex gap='0.5rem' wrap>
                {openExceptions.critical > 0 && (
                  <StatusPill status='danger'>{`Critical ×${openExceptions.critical}`}</StatusPill>
                )}
                {openExceptions.high > 0 && (
                  <StatusPill status='danger'>{`High ×${openExceptions.high}`}</StatusPill>
                )}
                {openExceptions.medium > 0 && (
                  <StatusPill status='accent'>{`Medium ×${openExceptions.medium}`}</StatusPill>
                )}
                {openExceptions.low > 0 && (
                  <StatusPill status='neutral'>{`Low ×${openExceptions.low}`}</StatusPill>
                )}
              </Flex>
            ) : undefined
          }
        />
      </Flex>

      <TablesGrid>
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Plan generation</TitleText>
          </ContainerHeader>
          <ContainerContent gap='1rem'>
            <OtherText grey>
              Plans are generated automatically from the work schedule (debounced, roughly every 30
              seconds). There is no manual trigger.
            </OtherText>
            {planGeneration ? (
              <Flex column gap='0.5rem'>
                <StatusPill
                  status={
                    planGeneration.status === FoodPlanGenerationStatus.Generated
                      ? 'success'
                      : planGeneration.status === FoodPlanGenerationStatus.Failed
                        ? 'danger'
                        : 'neutral'
                  }
                >
                  {planGenerationLabel(planGeneration.status)}
                </StatusPill>
                <OtherText grey>
                  Last run:{' '}
                  {planGeneration.lastRunAt
                    ? formatDateForDisplay(planGeneration.lastRunAt, true)
                    : '—'}
                </OtherText>
                <BodyText>
                  {`${planGeneration.recordsCreated} meals created · ${planGeneration.recordsCancelled} cancelled`}
                </BodyText>
                {planGeneration.message && <OtherText grey>{planGeneration.message}</OtherText>}
              </Flex>
            ) : (
              <OtherText grey>Plan generation has never run for this day.</OtherText>
            )}
          </ContainerContent>
        </Container>

        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Day close</TitleText>
          </ContainerHeader>
          <ContainerContent gap='1rem'>
            {dayClose?.canFinalize ? (
              <OtherText grey>All windows are ready — the day can be finalized.</OtherText>
            ) : (
              <Flex column gap='0.5rem'>
                <OtherText grey>Still blocking the day close:</OtherText>
                {(dayClose?.blockingReasons || []).map((reason) => (
                  <BodyText key={reason}>• {reason}</BodyText>
                ))}
                {!dayClose?.blockingReasons?.length && <OtherText grey>—</OtherText>}
              </Flex>
            )}
            <Button
              variant='primary'
              disabled={!dayClose?.canFinalize || finalizing}
              onClick={handleFinalizeDay}
            >
              {finalizing ? 'Finalizing…' : 'Finalize day'}
            </Button>
          </ContainerContent>
        </Container>
      </TablesGrid>

      <Container>
        <ContainerHeader>
          <TitleText weight='700'>Meal windows</TitleText>
        </ContainerHeader>
        <ContainerContent>
          <HonestTable
            columns={windowColumns}
            data={windowRows}
            keyField='id'
            loading={isLoading}
            emptyMessage='No meal windows for this day'
          />
        </ContainerContent>
      </Container>

      <Container>
        <ContainerHeader padding>
          <Flex between center width='100%' wrap gap='0.75rem'>
            <TitleText weight='700'>Planned intents preview</TitleText>
            <CalendarInput
              nomarg
              labelText='Day'
              defaultValue={previewDay}
              onChange={(d: Date) => d && setPreviewDay(d)}
            />
          </Flex>
        </ContainerHeader>
        <ContainerContent>
          <OtherText grey>
            Aggregated PLANNED counts only (name-free). After a window&apos;s cut-off fires, those
            meals lock to ORDERED and disappear from this preview.
          </OtherText>
          <HonestTable
            columns={previewColumns}
            data={previewRows}
            keyField='id'
            emptyMessage='No PLANNED intents for this day — the window may already be locked past cut-off'
          />
        </ContainerContent>
      </Container>
    </Flex>
  )
}
